import { useState, useEffect } from 'react';
import axios from 'axios';
import { Sprout } from 'lucide-react';
import { Link } from 'react-router-dom';
import { FarmCard } from '@/components/farms/FarmCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Farms() {
  const [farms, setFarms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFarms = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/farms`);
        setFarms(response.data);
      } catch (error) {
        console.error('Error fetching farms:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFarms();
  }, []);

  return (
    <div className="pt-20 min-h-screen bg-cream" data-testid="farms-page">
      {/* Header */}
      <section className="py-16 bg-dark relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1731113775405-d5734eb7361d?w=1920"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <h1 className="font-playfair text-4xl md:text-5xl font-bold text-cream mb-4" data-testid="farms-title">
            Meet Our Farms
          </h1>
          <p className="font-manrope text-cream/70 text-lg max-w-xl">
            Family-owned farms committed to sustainable practices and exceptional quality.
          </p>
        </div>
      </section>

      {/* Farms Grid */}
      <section className="section-padding">
        <div className="container-custom">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest"></div>
            </div>
          ) : farms.length === 0 ? (
            <div className="text-center py-24" data-testid="farms-empty-state">
              <Sprout className="w-16 h-16 text-dark/20 mx-auto mb-4" />
              <h3 className="font-playfair text-2xl font-bold text-dark mb-2">New farms coming soon</h3>
              <p className="font-manrope text-dark/60 max-w-md mx-auto">
                We're onboarding local NH farms one at a time. Are you a farmer or producer?{' '}
                <Link to="/for-farms" className="text-forest font-medium hover:underline">Partner with FarmBox</Link>.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="farms-grid">
              {farms.map((farm) => (
                <FarmCard key={farm.farm_id} farm={farm} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1760562796021-d8bbcb5bc318?w=800"
                alt="Hands holding fresh produce"
                className="rounded-[2rem] w-full"
              />
            </div>
            <div>
              <span className="text-xs font-manrope uppercase tracking-[0.2em] font-bold text-forest mb-4 block">Our Story</span>
              <h2 className="font-playfair text-4xl font-bold text-dark mb-6">
                From Our Farms to Your Table
              </h2>
              <p className="font-manrope text-dark/70 leading-relaxed mb-6">
                We partner directly with small farms across New Hampshire to bring you the freshest, most flavorful produce possible. Every farmer in our network shares our commitment to sustainable agriculture and ethical practices.
              </p>
              <p className="font-manrope text-dark/70 leading-relaxed mb-6">
                When you choose FarmBox, you're not just getting amazing food — you're supporting local families and preserving New Hampshire's agricultural heritage for future generations.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="bg-cream rounded-xl p-4 text-center">
                  <p className="font-playfair text-3xl font-bold text-forest">100%</p>
                  <p className="font-manrope text-sm text-dark/60">Local Farms</p>
                </div>
                <div className="bg-cream rounded-xl p-4 text-center">
                  <p className="font-playfair text-3xl font-bold text-forest">40+</p>
                  <p className="font-manrope text-sm text-dark/60">Years Combined</p>
                </div>
                <div className="bg-cream rounded-xl p-4 text-center">
                  <p className="font-playfair text-3xl font-bold text-forest">New</p>
                  <p className="font-manrope text-sm text-dark/60">Farm Partners Joining Soon</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
